import { dayjs } from '@musinsa/pbo-core'
import type { CollectFormValues } from '../queries/mapping'

// ── 수집 폼 값 이음매(시임) — 임시 ──────────────────────────────────────────────────
// 생성본(handoff/page.generated.tsx)의 수집 폼 컨트롤에는 아직 값 버스가 없다(조회영역 그룹 밖 폼 카드는
// codegen 이 `onChange={() => {}}` + TODO 로 낸다). 그래서 [수집] 슬롯은 ctx.form 을 받지 못한다.
// 생성본은 손대면 안 되므로(재핸드오프 때 덮인다) 배선이 **클릭 시점에 화면 DOM 에서 값을 읽는다.**
//
// TODO(codegen): 모달 밖 core.Form 에 폼 버스(formStore/formField)와 버튼 ctx.form 이 생기면 이 파일을 지우고
//   wiring.actions.gl02CollectButton 이 ctx.form 을 그대로 쓰게 바꾼다.
//
// 열쇠는 **라벨 텍스트**다(데모 저장본이 정한 '수집기간' 같은 문구). placeholder 는 쓰지 않는다 —
// 생성본 문자열과 정확일치 셀렉터가 어긋나는 것을 실측했다(2026-09-22). DOM 순서에도 기대지 않는다.
// 깨지는 조건: 데모가 라벨 문구를 바꾼다 → LABEL 을 같이 바꾼다(seam.test 가 먼저 붉어진다) ·
//   DS(pbo-core FormItem `.pbo-v2-fi*` / antd `.ant-select-selection-item`) 구조가 바뀐다.

const SELECTOR = {
  /** pbo-core FormItem 한 줄(라벨 + 컨트롤) */
  item: '.pbo-v2-fi',
  labelText: '.pbo-v2-fi-label-text',
  body: '.pbo-v2-fi-item',
  bodyInputs: '.pbo-v2-fi-item input',
} as const

export const LABEL = { period: '수집기간', orderNos: '주문번호', limit: '제한', lastDelivery: '마지막 배송 번호' } as const

/** 생성본 피커 표시 형식('YY-MM-DD' · 'YYYY-MM-DD' · 좁을 때 'MM-DD') → ISO 'YYYY-MM-DD'. 모르는 형식은 undefined. */
export const toIsoDate = (shown: string): string | undefined => {
  const v = shown.trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v
  const m2 = /^(\d{2})-(\d{2})-(\d{2})$/.exec(v)
  if (m2) return `20${m2[1]}-${m2[2]}-${m2[3]}`
  const md = /^(\d{2})-(\d{2})$/.exec(v)
  if (md) return `${dayjs().year()}-${md[1]}-${md[2]}`
  return undefined
}

const findItem = (root: ParentNode, label: string): Element | undefined =>
  Array.from(root.querySelectorAll(SELECTOR.item)).find((el) => el.querySelector(SELECTOR.labelText)?.textContent?.trim() === label)

const inputsOf = (item: Element | undefined): HTMLInputElement[] =>
  item ? Array.from(item.querySelectorAll<HTMLInputElement>(SELECTOR.bodyInputs)) : []

const readRange = (item: Element | undefined): [string, string] | undefined => {
  const [a, b] = inputsOf(item).map((i) => toIsoDate(i.value))
  return a && b ? [a, b] : undefined
}

const toNumber = (raw: string | undefined): number | undefined => {
  const v = raw?.trim()
  if (!v) return undefined
  const n = Number(v)
  return Number.isFinite(n) ? n : undefined
}

const toUniqueCodes = (raw: string | undefined): string[] =>
  Array.from(
    new Set(
      (raw ?? '')
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  )

export interface CollectFormRead {
  values: CollectFormValues
  /** 주문번호 입력 건수 — BE URL 길이 안전 상한(200건)을 화면에서도 같은 값으로 검증한다. */
  orderNoCount: number
  /** 마지막 배송 번호 고유 건수(dedup 후) — API 는 단건이라 2개 이상이면 배선이 막고 안내한다. 동일 번호 중복은 1건으로 센다. */
  lastDeliveryCount: number
  /** 마지막 배송 번호를 입력했지만 API 숫자 타입으로 변환할 수 없는지 여부. */
  lastDeliveryInvalid: boolean
}

/** 화면 DOM 에서 수집 폼 값을 읽는다. root = 생성본 뷰를 감싼 요소. */
export const readCollectForm = (root: ParentNode): CollectFormRead => {
  const period = readRange(findItem(root, LABEL.period))
  const limit = toNumber(inputsOf(findItem(root, LABEL.limit))[0]?.value)
  const orderNos = toUniqueCodes(inputsOf(findItem(root, LABEL.orderNos))[0]?.value)
  // 다건 코드 입력(MultiCodeInput) — 보이는 인풋 하나에 쉼표·공백으로 이어진 문자열. 팝오버 textarea 는 열렸을 때만 있고 input 이 아니다.
  const codes = toUniqueCodes(inputsOf(findItem(root, LABEL.lastDelivery))[0]?.value)
  const lastDeliveryItemId = toNumber(codes[0])
  return {
    orderNoCount: orderNos.length,
    lastDeliveryCount: codes.length,
    lastDeliveryInvalid: codes.length === 1 && lastDeliveryItemId === undefined,
    values: {
      collectPeriod: period ? [dayjs(period[0]), dayjs(period[1])] : undefined,
      orderNos: orderNos.length ? orderNos.join(',') : undefined,
      limit,
      lastDeliveryItemId,
    },
  }
}
